import { FC, useCallback, useMemo } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Button, Popover } from 'antd';
import * as React from 'react';
import { isInternalTech } from '../../utils/AuthUtils';
import {
	compareStrings,
	isAdminUser,
	nullSafeGet,
	nullSafeGetOrElse,
	recordHasContractorId,
} from '../../utils/DataAccessUtils';
import { INVOICE_STATUSES } from '../../constants/InvoiceStatuses';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getInvoiceSubUrl } from '../invoice_utilities/InvoiceUtilities';

const InvoiceEditButton: FC<any> = ({
	userType,
	currentUser,
	history,

	ghost = false,
	invoice,
	type = 'default',
	style = { marginLeft: 12 },
}) => {
	const workOrder = useMemo(() => nullSafeGet('workOrder', invoice), [invoice]);
	// A project invoice has no work order. Only the buyer V2 edit page understands one —
	// the legacy buyer form and the supplier form both post workOrderId and no projectId,
	// which the API rejects outright (it requires exactly one of the two).
	const isProjectInvoice = useMemo(
		() => !!nullSafeGet('projectId', invoice) && !nullSafeGet('workOrderId', invoice),
		[invoice]
	);
	const hasContractorId = useMemo(() => recordHasContractorId(invoice), [invoice]);
	const isAdmin = useMemo(() => isAdminUser(currentUser), [currentUser]);
	const isCurrentUserInvoiceSubmitter = useMemo(
		() => compareStrings(currentUser.email, nullSafeGetOrElse('createdBy', invoice, '')),
		[invoice, currentUser]
	);
	const invoicePathName = getInvoiceSubUrl(userType, currentUser, invoice);

	const isBuyerInvoice = useMemo(() => invoice.isBuyerUploaded || false, [invoice]);
	const isBuyerOrInternalTech = useMemo(
		() => userType === ROLE_TYPES.BUYER || isInternalTech(currentUser),
		[userType, currentUser]
	);
	const onClick = useCallback(
		() =>
			isProjectInvoice
				? history.push(`/${userType}/invoices/edit/${invoice.id}/v2`)
				: nullSafeGetOrElse('location.pathname', history, '').includes('/workOrders/detail/')
				? history.push(
						`/${userType}/workOrders/detail/${nullSafeGet('id', workOrder)}/invoices/${
							invoice.id
						}/edit${hasContractorId ? 'Contractor' : ''}?btwo=true`
				  )
				: history.push(
						`/${invoicePathName}/edit${hasContractorId ? 'Contractor' : ''}/${invoice.id}`
				  ),
		[history, invoice, isProjectInvoice, userType, workOrder, hasContractorId, invoicePathName]
	);

	const buttonProps = { style, onClick, ghost, type };

	return useMemo(() => {
		// The project-aware edit page is /buyer/invoices/edit/:id/v2, which is
		// BUYER_ADMIN_ONLY — so anyone else gets no action rather than one that opens onto
		// an authorization wall. That includes a non-admin buyer who submitted the invoice:
		// the status logic below would otherwise offer them Edit.
		//
		// This matches the legacy route, which is BUYER_ADMIN_ONLY too, so project invoices
		// behave exactly as work-order ones do. (The button does still offer Edit to a
		// non-admin submitter of a WORK-ORDER invoice, which hits the same wall — a
		// pre-existing mismatch, untouched here.)
		//
		// Checked inside the memo, not as an early return above it — bailing out before the
		// hook would change the hook count between renders.
		if (isProjectInvoice && (userType !== ROLE_TYPES.BUYER || !isAdmin)) {
			return null;
		}
		switch (invoice.status) {
			case INVOICE_STATUSES.draft:
				//External Supplier can edit
				return isBuyerOrInternalTech ? null : <Button {...buttonProps}>Edit</Button>;

			case INVOICE_STATUSES.pending:
				if (isBuyerOrInternalTech) {
					if (isBuyerInvoice) {
						if (isAdmin || isCurrentUserInvoiceSubmitter) {
							return <Button {...buttonProps}>Edit</Button>;
						} else {
							return null;
						}
					} else {
						return (
							<Popover
								content={'You can only edit invoices submitted by your company.'}
								placement={'left'}
							>
								<Button {...buttonProps} disabled={true}>
									Edit
								</Button>
							</Popover>
						);
					}
				} else {
					return (
						<Popover
							content={'Invoice must be moved back to draft status to edit it.'}
							placement={'left'}
						>
							<Button {...buttonProps} disabled={true}>
								Edit
							</Button>
						</Popover>
					);
				}
			case INVOICE_STATUSES.disputed:
				if (isBuyerOrInternalTech) {
					if (isBuyerInvoice) {
						if (isAdmin || isCurrentUserInvoiceSubmitter) {
							return <Button {...buttonProps}>Edit</Button>;
						} else {
							return null;
						}
					} else {
						return (
							<Popover
								content={'You can only edit invoices submitted by your company.'}
								placement={'left'}
							>
								<Button {...buttonProps} disabled={true}>
									Edit
								</Button>
							</Popover>
						);
					}
				} else {
					return <Button {...buttonProps}>Edit</Button>;
				}
			default:
				if (isBuyerOrInternalTech) {
					if (isBuyerInvoice) {
						if (isAdmin || isCurrentUserInvoiceSubmitter) {
							return (
								<Popover
									content={'Move this invoice back to pending status to edit it.'}
									placement={'left'}
									trigger={'hover'}
								>
									<Button {...buttonProps} disabled={true}>
										Edit
									</Button>
								</Popover>
							);
						} else {
							return null;
						}
					} else {
						return (
							<Popover
								content={'You can only edit invoices submitted by your company.'}
								placement={'left'}
							>
								<Button {...buttonProps} disabled={true}>
									Edit
								</Button>
							</Popover>
						);
					}
				} else {
					return (
						<Popover
							content={'Invoice must be moved back to draft status to edit it.'}
							placement={'left'}
						>
							<Button {...buttonProps} disabled={true}>
								Edit
							</Button>
						</Popover>
					);
				}
		}
	}, [
		isBuyerInvoice,
		isBuyerOrInternalTech,
		isAdmin,
		isCurrentUserInvoiceSubmitter,
		invoice,
		isProjectInvoice,
		userType,
		buttonProps,
	]);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	history: ownProps.history,
});

const ComponentWithoutUserType = withRouter(connect(mapStateToProps, {})(InvoiceEditButton));

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
